import React, {
    DetailedHTMLProps,
    HTMLAttributes,
    RefCallback,
    useEffect,
    useState,
} from 'react';
import Modal from '../modal/Modal';
import ModalHeader from '../modal/ModalHeader';
import ModalBody from '../modal/ModalBody';
import { ChromePicker } from 'react-color';
import { Token } from '../../reducers/map/mapReducer';
import Creatable from 'react-select/creatable';
import ErrorBoundary from '../ErrorBoundary';
import Client from '../../utils/network';
import Select from 'react-select';
import Button from '../UI/Button';

interface EditTokenModalProps {
    token?: Token,
    show: boolean,
    toggle: () => void,
    onChange: (val: Token) => void,
    onSubmit: () => void,
    onCancel: () => void,

}

interface SelectableUser {
    id: string,
    name: string,
    icon: string,
}

const customSingleValue = ({ data }: { data: { label: string, icon: string } }) => (
    <div className="flex items-center gap-2">
        <img src={data.icon} alt={data.label} className="w-6 h-6 rounded-xl" />
        {data.label}
    </div>
);

const customOption = (props:
    {
        data: { label: string, icon: string },
        innerRef: RefCallback<HTMLDivElement>,
        innerProps: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    },
) => {
    const { data, innerRef, innerProps } = props;
    return (
        <div ref={innerRef} {...innerProps} className="flex items-center gap-2 p-2 hover:bg-gray-200">
            <img src={data.icon} alt={data.label} className="w-6 h-6 rounded-xl" />
            {data.label}
        </div>
    );
};

const EditTokenModal = ({ token, show, onChange, toggle, onSubmit, onCancel }: EditTokenModalProps) => {

    const [availableUsers, setAvailableUsers] = useState<SelectableUser[]>([]);

    useEffect(() => {
        if (show) {
            Client.request('/api/available-users').then(async (res) => {
                const body = await res.json() as SelectableUser[];
                setAvailableUsers(body);
                return;
            }).catch(() => {
                // do nothing for now
            });
        }
    }, [show]);

    return (
        <Modal show={show} toggle={toggle}>
            <ModalHeader toggle={toggle}>Edit token</ModalHeader>
            <ErrorBoundary>
                {show && (
                    <ModalBody>
                        {token && (
                            <div className='flex items-stretch gap-4 flex-col justify-between'>
                                <label>
                                    <span>
                                        Border color
                                    </span>
                                    <ChromePicker
                                        color={`${token?.borderColor ?? '#000000'}`}
                                        className="!bg-mid-blue !text-white"
                                        onChange={(e) => {
                                            onChange({
                                                ...token,
                                                borderColor: `${e.hex}${Math.ceil((e.rgb.a ?? 0) * 255).toString(16)}`,
                                            });
                                        }}
                                    />
                                </label>

                                <label>
                                    <span>
                                        Statusses
                                    </span>
                                    <Creatable
                                        className='custom-select-container '
                                        classNamePrefix='custom-select'
                                        options={(token?.effects ?? [])}
                                        getOptionLabel={(e) => e.name}
                                        isMulti
                                        isClearable={false}
                                        onChange={(x) => onChange({
                                            ...token,
                                            effects: (x ?? []) as Token['effects'][number][],
                                        })}
                                        onCreateOption={(x) => {
                                            onChange({
                                                ...token,
                                                effects: [...(token?.effects ?? []), { name: x }],
                                            });
                                        }}
                                        value={token?.effects ?? []}
                                    />
                                </label>
                                <label>
                                    <span>
                                        Users
                                    </span>
                                    <Select
                                        className='custom-select-container '
                                        classNamePrefix='custom-select'
                                        options={availableUsers.map(user => ({
                                            value: user.id,
                                            label: user.name,
                                            icon: user.icon,
                                        }))}
                                        isMulti
                                        onChange={(selectedOptions) => {
                                            onChange({
                                                ...token,
                                                assignedUsers: selectedOptions.map(option => option.value),
                                            });
                                        }}
                                        value={token.assignedUsers?.map(userId => {
                                            const user = availableUsers.find(user => user.id === userId);
                                            return {
                                                value: userId,
                                                label: user?.name || userId,
                                                icon: user?.icon || '',
                                            };
                                        })}
                                        components={{
                                            SingleValue: customSingleValue,
                                            Option: customOption,
                                        }}
                                    />
                                </label>

                                <div className="flex gap-4 justify-end">
                                    <Button
                                        onClick={() => {
                                            onSubmit();
                                        }}
                                    >
                                        Apply
                                    </Button>
                                    <Button
                                        onClick={onCancel}
                                        flipped
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                    </ModalBody>
                )}
            </ErrorBoundary>
        </Modal>
    );
};

export default EditTokenModal;
